import { Component } from 'vue-property-decorator';
import { EComponentType, IComponent, IImageComponent, TComponentType } from '@/helpers/editor';
import EditorBase from './EditorBase';
import { Selector, Button } from 'streamlabs-beaker';
import Draggable from 'vuedraggable';
import { cloneDeep } from 'lodash-es';
import UUIDHelper from '@/helpers/uuid';
import { ISelector, ISelectorGroup } from '@/helpers/utils';
import Tooltip from '@/components/utils/Tooltip.vue';
import WebcamTemplate from '@/components/editor/templates/WebcamTemplate.vue';
import { EModalName } from '@/helpers/modal';
import ThemeLibraryModal from '@/components/editor/modals/ThemeLibraryModal.vue';
import ThemeApplyConfirmationModal from '@/components/editor/modals/ThemeApplyConfirmationModal.vue';
import ThemeResetConfirmationModal from '@/components/editor/modals/ThemeResetConfirmationModal.vue';
import StreamlabsApi from '@/apis/streamlabs-api';

@Component({
  components: {
    SSelector: Selector,
    SButton: Button,
    Tooltip,
    Draggable,
    WebcamTemplate,
    ThemeLibraryModal,
    ThemeResetConfirmationModal,
    ThemeApplyConfirmationModal,
  },
})
export default class Layer extends EditorBase {
  hoveredLayerId: number = null;

  async mounted() {
    if (!this.availableThemes) {
      const themeResponse = await StreamlabsApi.getThemes();
      if (themeResponse.themes) this.availableThemes = themeResponse.themes;
    }
  }

  get canUndoComponentUpdate() {
    return this.editorModule.componentUpdateUndoList.length;
  }

  get canRedoComponentUpdate() {
    return this.editorModule.componentUpdateRedoList.length;
  }

  onLayerClick(component: IComponent) {
    this.selectedComponent = component;
    this.selectedEditor = null;
  }

  getLayerName(layer: IComponent) {
    if (this.isImageComponent(layer) && (layer as IImageComponent).isBorder) return 'Border';
    if (this.isTextComponent(layer)) return layer.value;

    return layer.name;
  }

  onLayerDuplicate() {
    const componentToAdd = cloneDeep(this.selectedComponent);

    componentToAdd.id = UUIDHelper.create();
    componentToAdd.x += 50;
    componentToAdd.y += 50;

    const components = cloneDeep(this.components);
    components.push(componentToAdd);
    this.selectedScene = { ...this.selectedScene, components };

    this.selectedComponent = componentToAdd;
  }

  onLayerHide() {
    const data = {
      id: this.selectedComponent.id,
      hidden: !this.selectedComponent.hidden,
    };

    this.onComponentUpdate(data);
  }

  isActive(layer) {
    return (
      this.selectedComponent && this.selectedComponent.id && this.selectedComponent.id === layer.id
    );
  }

  shouldShowLayerActions(layer) {
    return this.isActive(layer) || layer.id === this.hoveredLayerId;
  }

  isHideable(layer) {
    if (!layer.hasOwnProperty('hideable')) return true;

    return layer.hideable;
  }

  isLocked(layer) {
    return layer.locked;
  }

  isHidden(layer) {
    return layer.hidden;
  }

  isHiddenHorizontally(layer): boolean {
    return layer.hidden;
  }

  isHiddenVertically(layer): boolean {
    return layer.verticalSettings?.hidden;
  }

  get componentListGroupOptions(): ISelectorGroup[] {
    const componentGroup = this.componentListGroup;
    const groupValues: ISelector[] = cloneDeep(componentGroup.simpleComponents);

    if (this.availableThemes) {
      groupValues.unshift({
        label: 'Theme',
        value: 'theme',
        iconClass: 'icon-pop-out-2',
      });
    }

    return [
      { groupValues },
      { groupLabel: 'Requires Streamlabs auth:', groupValues: componentGroup.streamlabsComponents },
    ];
  }

  onMouseEnterLayer(layer) {
    this.hoveredLayerId = layer.id;
  }

  onMouseLeaveLayer() {
    this.hoveredLayerId = null;
  }

  onLayerAdd(input: ISelector) {
    if (input.value === 'theme') {
      this.openThemeLibrary();
      return;
    }

    this.addComponent(input.value as TComponentType);
  }

  getLayerShouldDisplayWebcamPreview(layer): boolean {
    if (!layer || layer.type !== EComponentType.webcam) return false;

    return layer.preview;
  }

  openThemeResetConfirmationModal() {
    this.$modal.show(EModalName.themeResetConfirmation);
  }
}
